import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

export const cardStyle = { boxShadow: 'var(--v1-shadow)' } as const
export const cardCls = 'rounded-[12px] border border-[var(--color-border)] bg-[var(--color-surface)]'
export const mono = { fontFamily: 'var(--v1-font-mono)', fontVariantNumeric: 'tabular-nums' } as const

export function Card({ title, aside, children }: { title?: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className={`${cardCls} overflow-hidden`} style={cardStyle}>
      {title ? (
        <header className="flex h-12 items-center justify-between border-b border-[var(--color-border)] px-5">
          <h2 className="text-[15px] font-semibold tracking-[-0.005em]">{title}</h2>
          {aside}
        </header>
      ) : null}
      {children}
    </section>
  )
}

export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div
          key={k}
          className="flex min-h-11 items-center justify-between gap-4 border-t border-[var(--color-border)] px-5 py-2 first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
        >
          <dt className="text-[13px] text-[var(--color-muted)]">{k}</dt>
          <dd className="text-right text-[14px] font-medium">
            {v === null || v === undefined || v === '' ? <span className="text-[var(--color-muted)]">-</span> : v}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  right,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  right?: ReactNode
}) {
  return (
    <div className="flex items-end justify-between gap-6">
      <div>
        {eyebrow ? <div className="mb-2 text-[13px] text-[var(--color-muted)]">{eyebrow}</div> : null}
        <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.01em]">{title}</h1>
        {subtitle ? <p className="mt-1.5 text-[15px] text-[var(--color-muted)]">{subtitle}</p> : null}
      </div>
      {right}
    </div>
  )
}

/**
 * Link to a location-scoped path. The router's registered type only knows the top-level routes, so the
 * `$idOrCode` routes aren't in the typed `to` union; navigation is resolved at runtime by the real route tree.
 */
export function AppLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  return (
    <Link to={to as '/locations'} className={className}>
      {children}
    </Link>
  )
}

const crumbLink = 'text-[var(--color-accent)] hover:underline'

export function Crumb({ idOrCode, trail }: { idOrCode: string; trail: 'detail' | 'network' | 'wan' }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
      <Link to="/locations" className={crumbLink}>
        Locations
      </Link>
      <span aria-hidden="true">/</span>
      {trail === 'detail' ? (
        <span>{idOrCode}</span>
      ) : (
        <AppLink to={`/locations/${encodeURIComponent(idOrCode)}`} className={crumbLink}>
          {idOrCode}
        </AppLink>
      )}
      {trail === 'detail' ? null : (
        <>
          <span aria-hidden="true">/</span>
          {trail === 'network' ? (
            <span>Network</span>
          ) : (
            <>
              <AppLink to={`/locations/${encodeURIComponent(idOrCode)}/network`} className={crumbLink}>
                Network
              </AppLink>
              <span aria-hidden="true">/</span>
              <span>WAN settings</span>
            </>
          )}
        </>
      )}
    </nav>
  )
}

export function LinkButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <AppLink
      to={to}
      className="inline-flex h-9 items-center rounded-[10px] bg-[var(--color-accent)] px-4 text-[14px] font-medium text-[var(--color-accent-fg)] transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
    >
      {children}
    </AppLink>
  )
}

export function Notice({
  title,
  body,
  tone = 'neutral',
}: {
  title: string
  body?: string
  tone?: 'neutral' | 'error'
}) {
  return (
    <div
      className={`${cardCls} mt-8 px-6 py-14 text-center`}
      role={tone === 'error' ? 'alert' : undefined}
      style={cardStyle}
    >
      <p className="text-[15px] font-medium" style={tone === 'error' ? { color: 'var(--v1-bad)' } : undefined}>
        {title}
      </p>
      {body ? <p className="mt-1 text-[13px] text-[var(--color-muted)]">{body}</p> : null}
    </div>
  )
}

const SK_IDS = ['sk-a', 'sk-b', 'sk-c', 'sk-d']

export function SkeletonCards({ rows = 3 }: { rows?: number }) {
  return (
    <div className="mt-8 grid gap-5" aria-hidden="true">
      {SK_IDS.slice(0, rows).map((id) => (
        <div key={id} className={`${cardCls} p-5`} style={cardStyle}>
          <div className="v1-skeleton h-4 w-32" />
          <div className="v1-skeleton mt-4 h-3.5 w-3/4" />
          <div className="v1-skeleton mt-2.5 h-3.5 w-1/2" />
        </div>
      ))}
    </div>
  )
}

export type Tone = 'ok' | 'bad' | 'warn' | 'off'
const toneMap: Record<Tone, [string, string]> = {
  ok: ['var(--v1-ok)', 'var(--v1-ok-bg)'],
  bad: ['var(--v1-bad)', 'var(--v1-bad-bg)'],
  warn: ['var(--v1-warn)', 'var(--v1-warn-bg)'],
  off: ['var(--v1-off)', 'var(--v1-off-bg)'],
}

export function toneOf(status: string): Tone {
  const s = status.toUpperCase()
  if (['UP', 'ONLINE', 'ACTIVE', 'HEALTHY', 'PROVISIONED'].includes(s)) return 'ok'
  if (['DOWN', 'OFFLINE'].includes(s)) return 'bad'
  if (['DEGRADED', 'CONFIGURING', 'DISPATCHING'].includes(s)) return 'warn'
  return 'off'
}

export function Badge({ label, tone }: { label: string; tone?: Tone }) {
  const [fg, bg] = toneMap[tone ?? toneOf(label)]
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[12px] font-medium"
      style={{ color: fg, background: bg }}
    >
      <span className="size-1.5 rounded-full" style={{ background: 'currentColor' }} />
      {label}
    </span>
  )
}

export function Stat({ label, value, tone }: { label: string; value: number; tone?: Tone }) {
  return (
    <div className={`${cardCls} px-5 py-4`} style={cardStyle}>
      <div className="text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]">{label}</div>
      <div
        className="mt-1 text-[28px] font-semibold leading-tight"
        style={{ color: tone ? toneMap[tone][0] : undefined }}
      >
        {value}
      </div>
    </div>
  )
}
